import { countRequiredBlankFields } from '../../domain/draftInvalidation.js';
import type { AiFillPatch, AiFillResult, WorldDraft } from '../../domain/types.js';
import { applyAiFillToDraft } from './draftReducer.js';
import { buildAiFillPatchesForStep, suggestForQuestion, type FallbackSuggestion } from './fallbacks.js';
import { WIZARD_QUESTION_IDS } from '../../constants.js';
import { GROUNDING_FIELDS, CONNECTED_QUESTIONS } from './questionSchema.js';

/** Hook surface for future DeepSeek suggestions — local fallbacks when unavailable. */
export interface WorldSuggestionRequest {
  field: 'grounding' | 'connected' | 'question' | 'time-period' | 'schedule';
  draft: WorldDraft;
  questionId?: string;
}

export interface WorldSuggestionProvider {
  suggest(request: WorldSuggestionRequest): Promise<FallbackSuggestion | null>;
  readonly available: boolean;
}

export const localSuggestionProvider: WorldSuggestionProvider = {
  available: false,
  async suggest(request: WorldSuggestionRequest): Promise<FallbackSuggestion | null> {
    if (request.field === 'question' && request.questionId) {
      return suggestForQuestion(request.draft, request.questionId);
    }
    return null;
  },
};

export interface StepBlankSummary {
  count: number;
  labels: { fieldId: string; label: string }[];
}

export function blankSummaryForStep(
  draft: WorldDraft,
  step: 'location-grounding' | 'connected',
): StepBlankSummary {
  const { blanks } = countRequiredBlankFields(draft);
  const allowedIds = new Set(
    step === 'location-grounding'
      ? GROUNDING_FIELDS.map((f) => f.questionId)
      : CONNECTED_QUESTIONS.filter((q) => q.required).map((q) => q.questionId),
  );
  const labels = blanks.filter((b) => allowedIds.has(b.fieldId));
  if (step === 'connected') {
    const crowd = draft.answers[WIZARD_QUESTION_IDS.connectedCrowd];
    const rhythm = draft.answers[WIZARD_QUESTION_IDS.connectedRhythm];
    if ((!crowd || crowd.status === 'unanswered') && !labels.some((l) => l.fieldId === WIZARD_QUESTION_IDS.connectedCrowd)) {
      labels.push({ fieldId: WIZARD_QUESTION_IDS.connectedCrowd, label: 'Who typically comes here' });
    }
    if ((!rhythm || rhythm.status === 'unanswered') && !labels.some((l) => l.fieldId === WIZARD_QUESTION_IDS.connectedRhythm)) {
      labels.push({ fieldId: WIZARD_QUESTION_IDS.connectedRhythm, label: 'Daily rhythm' });
    }
  }
  return { count: labels.length, labels };
}

export function patchesForStep(
  draft: WorldDraft,
  step: 'location-grounding' | 'connected',
): AiFillPatch[] {
  return buildAiFillPatchesForStep(draft, step);
}

export interface AiFillStepResult {
  draft: WorldDraft;
  result: AiFillResult;
  summary: string;
  nothingToFill: boolean;
  filledLabels: string[];
}

export function applyLocalAiFillForStep(
  draft: WorldDraft,
  step: 'location-grounding' | 'connected',
): AiFillStepResult {
  const { count, labels } = blankSummaryForStep(draft, step);
  if (count === 0) {
    return {
      draft,
      result: { applied: [], rejected: [] },
      summary: 'Nothing to fill in',
      nothingToFill: true,
      filledLabels: [],
    };
  }

  const patches = patchesForStep(draft, step);
  const { draft: next, result } = applyAiFillToDraft(draft, patches);
  const filledLabels = result.applied.map((p) => {
    const match = labels.find((l) => l.fieldId === p.questionId);
    return match?.label ?? p.questionId;
  });

  const summary =
    filledLabels.length > 0
      ? `Filled ${filledLabels.length} blank${filledLabels.length === 1 ? '' : 's'}: ${filledLabels.join(', ')}`
      : 'Nothing to fill in';

  return {
    draft: next,
    result,
    summary,
    nothingToFill: filledLabels.length === 0,
    filledLabels,
  };
}

export function labelForFieldId(fieldId: string): string {
  const grounding = GROUNDING_FIELDS.find((f) => f.questionId === fieldId);
  if (grounding) return grounding.label;
  const connected = CONNECTED_QUESTIONS.find((q) => q.questionId === fieldId);
  return connected?.prompt ?? fieldId;
}
